import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Alert, Badge, Button, Empty, LinkButton, Loading, PageHeader } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { whatsappUrl } from "../../lib/format";
import type { TeacherStudentItem } from "../../lib/types";

export const SUB_STATUS: Record<
  string,
  { label: string; color: "green" | "gold" | "red" | "gray" }
> = {
  active: { label: "Ativa", color: "green" },
  overdue: { label: "Em atraso", color: "gold" },
  suspended: { label: "Suspensa", color: "red" },
  cancelled: { label: "Cancelada", color: "gray" },
};

export function TeacherStudents() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["teacher-students"],
    queryFn: () => api.get<{ items: TeacherStudentItem[] }>("/v1/teachers/me/students"),
  });
  const contact = useMutation({
    mutationFn: (id: string) => api.patch(`/v1/students/${id}`, { firstContactDone: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teacher-students"] }),
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert kind="risk">{errorMessage(q.error)}</Alert>;
  const items = q.data.items;
  const fresh = items.filter((s) => s.isNew);
  return (
    <>
      <PageHeader title="Meus alunos" subtitle={`${items.length} aluno(s) com horário semanal.`} />
      {fresh.length > 0 ? (
        <Alert kind="warning" title="Alunos novos">
          Chame no WhatsApp em até 1 dia útil, confirme o horário e mande o convite do Google
          Classroom. Depois toque em "Primeiro contato feito".
        </Alert>
      ) : null}
      {items.length === 0 ? (
        <Empty>Quando um aluno pagar, ele aparece aqui com o horário reservado.</Empty>
      ) : null}
      <div className="stack" style={{ marginTop: 12 }}>
        {items.map((s) => {
          const st = SUB_STATUS[s.subscriptionStatus] ?? {
            label: s.subscriptionStatus,
            color: "gray" as const,
          };
          return (
            <div
              key={s.id}
              className={`card tight${s.isNew ? " " : ""}`}
              style={
                s.isNew ? { borderColor: "var(--gold)", background: "var(--gold-soft)" } : undefined
              }
            >
              <div className="row between">
                <div className="stack" style={{ gap: 2 }}>
                  <Link
                    to={`/professor/alunos/${s.id}`}
                    className="strong"
                    style={{ color: "var(--navy)", fontSize: 17 }}
                  >
                    {s.name}
                  </Link>
                  <span className="small muted">
                    {s.age ? `${s.age} anos · ` : ""}
                    {s.cefrLevel ?? "nível não informado"} · {s.planName}
                    {s.partnerName ? ` com ${s.partnerName}` : ""}
                  </span>
                  <span className="small">{s.scheduleLabel}</span>
                </div>
                <div className="row">
                  {s.isNew ? <Badge color="gold">Novo</Badge> : null}
                  <Badge color={st.color}>{st.label}</Badge>
                </div>
              </div>
              <div className="row" style={{ marginTop: 10 }}>
                {s.phone ? (
                  <LinkButton
                    size="small"
                    variant="whatsapp"
                    href={whatsappUrl(s.phone)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    WhatsApp
                  </LinkButton>
                ) : null}
                {s.classroomUrl ? (
                  <LinkButton
                    size="small"
                    variant="secondary"
                    href={s.classroomUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Classroom
                  </LinkButton>
                ) : null}
                {s.isNew ? (
                  <Button
                    size="small"
                    loading={contact.isPending && contact.variables === s.id}
                    onClick={() => contact.mutate(s.id)}
                  >
                    Primeiro contato feito
                  </Button>
                ) : null}
                <span className="small muted" style={{ marginLeft: "auto" }}>
                  IA:{" "}
                  {s.quota
                    ? `${Math.floor(s.quota.usedMinutes)} de ${s.quota.allowedMinutes} min`
                    : "sem cota"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
